import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatError, photoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Heart, MapPin, GraduationCap, Briefcase, Users, Sparkles, ArrowLeft, MessageCircle, BadgeCheck, Lock, Mail, Phone, Crown, Check, Ban } from "lucide-react";
import { toast } from "sonner";
import ReportDialog from "@/components/ReportDialog";
import { useLanguage } from "@/context/LanguageContext";

export default function ProfileDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [acting, setActing] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/profile/${id}`); setProfile(data); }
      catch (e) { toast.error(formatError(e)); }
    })();
  }, [id]);

  const sendInterest = async () => {
    setActing(true);
    try {
      await api.post(`/interests/send/${id}`);
      toast.success(language === "hi" ? "दिलचस्पी भेजी गई" : "Interest sent");
      setProfile(p => ({ ...p, interest: { status: "pending", from_me: true } }));
    } catch (e) { toast.error(formatError(e)); }
    finally { setActing(false); }
  };

  const acceptInterest = async () => {
    setActing(true);
    try {
      await api.post(`/interests/${profile.interest.interest_id}/respond?action=accept`);
      toast.success(language === "hi" ? "दिलचस्पी स्वीकार की गई" : "Interest accepted!");
      setShowMatchModal(true);
      // Reload profile to reflect mutual match status and unlock contacts
      const { data } = await api.get(`/profile/${id}`);
      setProfile(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setActing(false); }
  };

  const toggleShortlist = async () => {
    setActing(true);
    try {
      const { data } = await api.post(`/shortlist/${id}`);
      const isAdded = data.op === "added";
      toast.success(
        language === "hi" 
          ? (isAdded ? "शॉर्टलिस्ट में जोड़ा गया" : "शॉर्टलिस्ट से हटाया गया")
          : (isAdded ? "Added to shortlist" : "Removed from shortlist")
      );
      setProfile(p => ({ ...p, is_shortlisted: isAdded }));
    } catch (e) { toast.error(formatError(e)); }
    finally { setActing(false); }
  };

  const toggleBlock = async () => {
    setActing(true);
    try {
      const endpoint = profile.is_blocked ? `/privacy/unblock/${id}` : `/privacy/block/${id}`;
      await api.post(endpoint);
      const nowBlocked = !profile.is_blocked;
      toast.success(
        language === "hi"
          ? (nowBlocked ? "यूज़र को ब्लॉक कर दिया गया है" : "यूज़र को अनब्लॉक कर दिया गया है")
          : (nowBlocked ? "User blocked successfully" : "User unblocked successfully")
      );
      setProfile(p => ({ ...p, is_blocked: nowBlocked }));
    } catch (e) { toast.error(formatError(e)); }
    finally { setActing(false); }
  };

  if (!profile) return <div className="container-px py-12 text-muted-foreground">Loading...</div>;
  const rawPhotos = profile.photos?.length ? profile.photos : ["https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=900"];
  const photos = rawPhotos.map(photoUrl);

  return (
    <div className="container-px py-8">
      <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6" data-testid="profile-back"><ArrowLeft className="w-4 h-4 mr-1"/>Back</button>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Photos */}
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden card-soft">
            <img src={photos[photoIdx]} alt={profile.name} className="w-full h-full object-cover" />
            {!profile.photos?.length && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <Button
                  onClick={async () => {
                    setActing(true);
                    try {
                      await api.post(`/profile/${profile.user_id}/request-photo`);
                      toast.success(language === "hi" ? `फ़ोटो अनुरोध ${profile.name} को भेज दिया गया है!` : `Photo request sent to ${profile.name}!`);
                    } catch (e) {
                      toast.error(formatError(e));
                    } finally {
                      setActing(false);
                    }
                  }}
                  disabled={acting}
                  className="rounded-full bg-white text-black hover:bg-white/90 btn-press px-6 py-3 font-semibold shadow-lg text-sm border-2 border-white"
                  data-testid={`btn-request-photo-${profile.user_id}`}
                >
                  📷 {language === "hi" ? "फ़ोटो का अनुरोध करें" : "Request Photo"}
                </Button>
              </div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setPhotoIdx(i)} className={`w-20 h-20 shrink-0 rounded-xl overflow-hidden border-2 ${i === photoIdx ? "border-primary" : "border-transparent"}`} data-testid={`photo-thumb-${i}`}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="lg:col-span-7">
          <span className="label-cap">{profile.district} · Jharkhand</span>
          <h1 className="font-heading text-5xl text-primary mt-2 flex items-center gap-2">
            {profile.name}
            {profile.is_verified && (
              <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full" data-testid="detail-verified-badge">
                <BadgeCheck className="w-4 h-4" /> Verified
              </span>
            )}
          </h1>
          <p className="text-muted-foreground mt-2">{profile.age ? `${profile.age} yrs · ` : ""}{profile.height_cm ? `${profile.height_cm} cm · ` : ""}{profile.marital_status}</p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {/* Interest Button States */}
            {!profile.interest ? (
              <Button 
                onClick={sendInterest} 
                disabled={acting || profile.is_blocked} 
                className="rounded-full bg-primary hover:bg-primary/90 btn-press font-semibold shadow-md" 
                data-testid="send-interest-btn"
              >
                <Heart className="w-4 h-4 mr-1.5" />
                {language === "hi" ? "दिलचस्पी भेजें" : "Send interest"}
              </Button>
            ) : profile.interest.status === "pending" ? (
              profile.interest.from_me ? (
                <Button 
                  disabled 
                  variant="secondary" 
                  className="rounded-full font-semibold border border-border bg-muted/40 text-muted-foreground"
                >
                  <Check className="w-4 h-4 mr-1.5 text-emerald-600" />
                  {language === "hi" ? "दिलचस्पी भेजी गई" : "Interest Sent"}
                </Button>
              ) : (
                <Button 
                  onClick={acceptInterest} 
                  disabled={acting} 
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white btn-press font-semibold shadow-md"
                >
                  <Heart className="w-4 h-4 mr-1.5" fill="currentColor" />
                  {language === "hi" ? "स्वीकार करें" : "Accept Interest"}
                </Button>
              )
            ) : profile.interest.status === "accepted" ? (
              <Button 
                disabled 
                variant="outline" 
                className="rounded-full font-bold border-emerald-200 bg-emerald-50/50 text-emerald-700 shadow-sm"
              >
                <Heart className="w-4 h-4 mr-1.5 text-emerald-600" fill="currentColor" />
                {language === "hi" ? "कनेक्टेड (स्वीकृत)" : "Connected (Mutual)"}
              </Button>
            ) : (
              <Button onClick={sendInterest} disabled={acting} className="rounded-full bg-primary" data-testid="send-interest-btn">
                <Heart className="w-4 h-4 mr-1.5" />
                {language === "hi" ? "दिलचस्पी भेजें" : "Send interest"}
              </Button>
            )}

            {/* Shortlist Button */}
            <Button 
              onClick={toggleShortlist} 
              disabled={acting} 
              variant={profile.is_shortlisted ? "secondary" : "outline"} 
              className={`rounded-full btn-press font-semibold ${profile.is_shortlisted ? "border-secondary/20 text-secondary bg-secondary/10" : ""}`} 
              data-testid="shortlist-btn"
            >
              <Sparkles className={`w-4 h-4 mr-1.5 ${profile.is_shortlisted ? "text-secondary fill-secondary" : "text-muted-foreground"}`}/>
              {profile.is_shortlisted 
                ? (language === "hi" ? "शॉर्टलिस्टेड" : "Shortlisted") 
                : (language === "hi" ? "शॉर्टलिस्ट" : "Shortlist")}
            </Button>

            {/* Message Button */}
            <Button 
              onClick={() => {
                if (profile.interest?.status === "accepted") {
                  nav(`/app/chat/${profile.user_id}`);
                } else {
                  toast.info(language === "hi" 
                    ? "आपस में दिलचस्पी स्वीकार होने के बाद चैट अनलॉक होगी।" 
                    : "Chat unlocks once mutual interest is accepted.");
                }
              }} 
              variant="outline" 
              className={`rounded-full btn-press font-semibold ${profile.interest?.status === "accepted" ? "border-primary text-primary" : "text-muted-foreground/60"}`}
              data-testid="message-btn"
            >
              <MessageCircle className="w-4 h-4 mr-1.5"/>
              {language === "hi" ? "चैट" : "Message"}
              {profile.interest?.status !== "accepted" && <Lock className="w-3.5 h-3.5 ml-1.5 opacity-60" />}
            </Button>

            {/* Report Dialog */}
            <ReportDialog targetUserId={profile.user_id} />

            {/* Block Button */}
            <Button 
              onClick={toggleBlock} 
              disabled={acting} 
              variant="ghost" 
              className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 btn-press text-xs font-semibold"
              data-testid="block-btn"
            >
              <Ban className="w-3.5 h-3.5 mr-1" />
              {profile.is_blocked ? (language === "hi" ? "अनब्लॉक करें" : "Unblock") : (language === "hi" ? "ब्लॉक करें" : "Block")}
            </Button>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <Info icon={MapPin} label="Lives in" value={[profile.village, profile.block, profile.district].filter(Boolean).join(", ") || "—"} />
            <Info icon={GraduationCap} label="Education" value={profile.education} />
            <Info icon={Briefcase} label="Occupation" value={profile.occupation} />
            <Info icon={Users} label="Family" value={profile.family_type} />
            <Info label="Religion" value={`${profile.religion || "—"}${profile.caste ? ` · ${profile.caste}` : ""}`} />
            <Info label="Mother tongue" value={profile.mother_tongue} />
            <Info label="Diet" value={profile.diet} />
            <Info label="Income" value={profile.income} />
          </div>

          {profile.about && (
            <div className="mt-8">
              <span className="label-cap">About</span>
              <p className="mt-2 leading-relaxed text-foreground">{profile.about}</p>
            </div>
          )}

          <div className="mt-8">
            <span className="label-cap">Horoscope</span>
            <div className="mt-2 grid sm:grid-cols-3 gap-3">
              <Chip label="Rashi" value={profile.rashi} />
              <Chip label="Nakshatra" value={profile.nakshatra} />
              <Chip label="Manglik" value={profile.manglik} />
            </div>
            {profile.birth_time && (
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <Chip label="Birth Time" value={profile.birth_time} />
                {profile.birth_place && <Chip label="Birth Place" value={profile.birth_place} />}
              </div>
            )}
          </div>

          {/* Contact Details Section */}
          <div className="mt-8 border-t border-border/60 pt-6">
            <span className="label-cap">Contact Details</span>
            {profile.contact_locked ? (
              <div className="mt-3 p-5 rounded-2xl bg-secondary/5 border-2 border-dashed border-secondary/25 text-center max-w-md">
                <Lock className="w-8 h-8 text-secondary mx-auto mb-3" />
                <h3 className="font-heading text-lg text-primary font-bold">Contact Details Locked</h3>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Free users can only view 3 mutual contacts. Upgrade to Premium to see unlimited contacts and communicate instantly.
                </p>
                <Button asChild className="mt-4 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold shadow-md" data-testid="profile-detail-upgrade">
                  <Link to="/app/premium">
                    <Crown className="w-4 h-4 mr-1.5" /> Upgrade to Premium
                  </Link>
                </Button>
              </div>
            ) : (profile.phone || profile.email) ? (
              <div className="mt-3 p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/15 space-y-3 text-sm max-w-md">
                <div className="flex items-center gap-3 text-primary">
                  <Phone className="w-4 h-4 text-secondary shrink-0" />
                  <div>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground block">Phone</span>
                    <a href={`tel:${profile.phone}`} className="font-semibold hover:underline select-all">{profile.phone || "—"}</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-primary">
                  <Mail className="w-4 h-4 text-secondary shrink-0" />
                  <div>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground block">Email</span>
                    <a href={`mailto:${profile.email}`} className="font-semibold hover:underline select-all">{profile.email || "—"}</a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-3 p-4 rounded-2xl bg-muted/20 border border-border/40 text-muted-foreground text-xs max-w-md">
                Contact details will unlock automatically once you have a mutual accepted interest.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Mutual Match Celebration Modal ── */}
      {showMatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="text-center max-w-sm w-full space-y-6 animate-in zoom-in-95 duration-300">
            {/* Celebration Sparkles */}
            <div className="relative h-20 flex justify-center">
              <Sparkles className="w-16 h-16 text-amber-400 animate-pulse animate-spin" style={{ animationDuration: '4s' }} />
            </div>
            
            <div className="space-y-2">
              <h2 className="font-heading text-4xl text-white font-black tracking-wide">
                {language === "hi" ? "आपस में मेल मिला!" : "It's a Match!"}
              </h2>
              <p className="text-sm text-white/80">
                {language === "hi" 
                  ? `आप और ${profile.name} एक दूसरे में रुचि रखते हैं।`
                  : `You and ${profile.name} have expressed mutual interest in each other.`}
              </p>
            </div>

            {/* Overlapping Avatars */}
            <div className="flex justify-center items-center py-6">
              <div className="relative flex items-center justify-center w-48 h-28">
                {/* Current User Photo */}
                <div className="absolute left-4 w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-2xl rotate-[-6deg] hover:rotate-0 transition-transform duration-300 bg-white">
                  <img 
                    src={photoUrl(user?.photos?.[0]) || "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=150"} 
                    alt="You" 
                    className="w-full h-full object-cover" 
                  />
                </div>
                {/* Heart Icon in Center */}
                <div className="absolute z-10 w-12 h-12 rounded-full bg-primary flex items-center justify-center border-4 border-white shadow-lg animate-bounce">
                  <Heart className="w-6 h-6 text-white" fill="currentColor" />
                </div>
                {/* Target User Photo */}
                <div className="absolute right-4 w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-2xl rotate-[6deg] hover:rotate-0 transition-transform duration-300 bg-white">
                  <img 
                    src={photoUrl(profile.photos?.[0]) || "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=150"} 
                    alt={profile.name} 
                    className="w-full h-full object-cover" 
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-4">
              <Button 
                onClick={() => {
                  setShowMatchModal(false);
                  nav(`/app/chat/${profile.user_id}`);
                }}
                className="w-full rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold py-3 shadow-lg btn-press"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                {language === "hi" ? "चैट शुरू करें" : "Send Message"}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowMatchModal(false)}
                className="w-full rounded-full text-white/60 hover:text-white hover:bg-white/10 text-xs font-semibold py-2.5"
              >
                {language === "hi" ? "बाद में" : "Keep Browsing"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3">
      {Icon && <div className="mt-1"><Icon className="w-4 h-4 text-primary" strokeWidth={1.5}/></div>}
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );
}
function Chip({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-accent/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-secondary font-semibold">{label}</div>
      <div className="text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}
